import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { findingRefRanges, findingRefAt } from './findingRefRanges';
import type { FindingRefRange } from './findingRefRanges';

/**
 * Marks the Befund references in an editor as chips — bold number inside a
 * ring — and reports a click on one.
 *
 * DECORATIONS, NOT NODES. The references stay ordinary, editable text: this
 * only paints over the document, it never changes it. The field's value is
 * plain text that the ExcaBook round-trip reads back, so a reference that became
 * a document node would have to serialise itself back into the exact same string
 * on every keystroke; a decoration has nothing to serialise. It also leaves the
 * caret free to sit inside a reference.
 *
 * What that costs: the mark is CSS, not a React component — `Decoration.inline`
 * takes attributes only. The classes below are styled from the MUI theme in
 * RefTextEditor.
 */

export const CLASS_REF = 'ast-finding-ref';
export const CLASS_REF_NUMBER = 'ast-finding-ref-number';
export const CLASS_REF_LINKED = 'ast-finding-ref-linked';
/** A Gesamtbeschreibung rather than a Befund — drawn with squarer corners. */
export const CLASS_REF_OVERALL = 'ast-finding-ref-overall';
/** The opening and closing pieces of one chip — see the note on segments. */
export const CLASS_REF_FIRST = 'ast-finding-ref-first';
export const CLASS_REF_LAST = 'ast-finding-ref-last';
/**
 * On the editor's container while Ctrl/Cmd is held — the only time a chip is
 * clickable, so the only time it should look it.
 */
export const CLASS_MOD_HELD = 'ast-mod-held';

export const findingRefPluginKey = new PluginKey<DecorationSet>(
    'ast-finding-refs'
);

/** Transaction meta asking for a redraw without the document having changed. */
const REFRESH = 'refresh';

/**
 * The callbacks, held in the extension's STORAGE rather than its options.
 *
 * Storage is a mutable object the editor owns, which is what is needed here: the
 * plugin is created once (rebuilding the extension list recreates the editor and
 * drops the caret), but `resolves` depends on records that arrive from the
 * database later. Reading it out of storage means the plugin always asks the
 * CURRENT function.
 */
export interface FindingRefStorage {
    /**
     * Whether this reference points at a record that exists. One that resolves
     * is drawn as a link and reports its clicks; one that does not is still
     * marked, which is how a typo in a Tagebuch shows itself.
     */
    resolves: ((range: FindingRefRange) => boolean) | null;
    /** A click on a resolving reference. */
    onOpen: ((range: FindingRefRange) => void) | null;
    /**
     * Native tooltip for a resolving reference — how the reader learns that
     * Ctrl+click does anything at all. Supplied from outside because the wording
     * is translated and the plugin has no intl.
     */
    titleFor: ((range: FindingRefRange) => string) | null;
}

declare module '@tiptap/core' {
    interface Storage {
        findingRefDecoration: FindingRefStorage;
    }
    interface Commands<ReturnType> {
        findingRefDecoration: {
            /**
             * Hand the plugin the callbacks it should use from now on, and
             * redraw with them. A COMMAND rather than the component writing to
             * `editor.storage` itself: the two halves belong together — new
             * callbacks are only visible once something redraws.
             */
            setFindingRefHandlers: (handlers: FindingRefStorage) => ReturnType;
        };
    }
}

export const FindingRefDecoration = Extension.create<
    Record<string, never>,
    FindingRefStorage
>({
    name: 'findingRefDecoration',

    addStorage() {
        return { resolves: null, onOpen: null, titleFor: null };
    },

    addCommands() {
        return {
            setFindingRefHandlers:
                (handlers: FindingRefStorage) =>
                ({ tr, dispatch }) => {
                    this.storage.resolves = handlers.resolves;
                    this.storage.onOpen = handlers.onOpen;
                    this.storage.titleFor = handlers.titleFor;
                    if (dispatch) {
                        tr.setMeta(findingRefPluginKey, REFRESH);
                    }
                    return true;
                }
        };
    },

    addProseMirrorPlugins() {
        const storage = this.storage;
        const resolves = (range: FindingRefRange) =>
            storage.resolves?.(range) ?? false;
        const onOpen = (range: FindingRefRange) => storage.onOpen?.(range);
        const titleFor = (range: FindingRefRange) =>
            storage.titleFor?.(range) ?? '';
        /** Ranges of the CURRENT document — recomputed with the state. */
        let ranges: FindingRefRange[] = [];

        /**
         * ONE chip is drawn as SEVERAL decorations, on purpose.
         *
         * ProseMirror renders overlapping inline decorations as ADJACENT spans,
         * not nested ones: a decoration over the whole reference plus one over
         * its number comes out as "Bef. " and "7000" side by side, each carrying
         * both classes — and a border on that class then rings EVERY piece.
         *
         * So the split is done here instead, and each piece is told whether it
         * opens or closes the chip. The CSS gives every piece the top and bottom
         * rule and only the ends the sides and the radius.
         */
        const decorate = (doc: Parameters<typeof findingRefRanges>[0]) => {
            ranges = findingRefRanges(doc);
            const decorations = ranges.flatMap((range) => {
                const linked = resolves(range);
                // On EVERY piece, so the tooltip shows wherever the pointer
                // rests on the chip rather than only over its first word.
                const title = linked ? titleFor(range) : '';
                const pieces: { from: number; to: number; number: boolean }[] =
                    [];
                if (range.numberFrom > range.from) {
                    pieces.push({
                        from: range.from,
                        to: range.numberFrom,
                        number: false
                    });
                }
                pieces.push({
                    from: range.numberFrom,
                    to: range.numberTo,
                    number: true
                });
                if (range.to > range.numberTo) {
                    pieces.push({
                        from: range.numberTo,
                        to: range.to,
                        number: false
                    });
                }
                return pieces.map((piece, index) =>
                    Decoration.inline(piece.from, piece.to, {
                        ...(title ? { title } : {}),
                        class: [
                            CLASS_REF,
                            linked ? CLASS_REF_LINKED : '',
                            range.ref.overallDescription
                                ? CLASS_REF_OVERALL
                                : '',
                            piece.number ? CLASS_REF_NUMBER : '',
                            index === 0 ? CLASS_REF_FIRST : '',
                            index === pieces.length - 1 ? CLASS_REF_LAST : ''
                        ]
                            .filter(Boolean)
                            .join(' ')
                    })
                );
            });
            return DecorationSet.create(doc, decorations);
        };

        return [
            new Plugin({
                key: findingRefPluginKey,
                state: {
                    init: (_config, state) => decorate(state.doc),
                    /**
                     * Recomputed on every change to the DOCUMENT, which is what
                     * makes the chips appear as the number is typed. Moving the
                     * caret changes no text and must not rescan.
                     *
                     * The `refresh` meta is the other way in: whether a
                     * reference RESOLVES depends on data outside the editor, and
                     * a Befund created while somebody types would otherwise stay
                     * unlinked until the next keystroke redrew it.
                     */
                    apply: (transaction, value, _old, state) =>
                        transaction.docChanged ||
                        transaction.getMeta(findingRefPluginKey) === REFRESH
                            ? decorate(state.doc)
                            : value
                },
                props: {
                    decorations(state) {
                        return findingRefPluginKey.getState(state);
                    },
                    handleClick(_view, pos, event) {
                        /**
                         * A chip sits in the middle of a sentence somebody is
                         * writing, so a PLAIN click has to keep meaning "put
                         * the caret here". Ctrl (Cmd on a Mac) is the modifier
                         * editors use for exactly this, and the pointer only
                         * appears while it is held (see CLASS_MOD_HELD).
                         */
                        if (!event.ctrlKey && !event.metaKey) {
                            return false;
                        }
                        const range = findingRefAt(ranges, pos);
                        if (!range || !resolves(range)) {
                            // Not a reference, or one that points nowhere:
                            // let the editor place the caret as usual.
                            return false;
                        }
                        onOpen(range);
                        return true;
                    }
                }
            })
        ];
    }
});
